import { useEffect, useRef } from "react";
import { Overline, Reveal } from "./Reveal";

const CENTRES = [
  { city: "London", lat: 51.5072, lon: -0.1276, coords: "51.5072°N · 0.1276°W" },
  { city: "Dubai", lat: 25.2048, lon: 55.2708, coords: "25.2048°N · 55.2708°E" },
  { city: "Singapore", lat: 1.3521, lon: 103.8198, coords: "1.3521°N · 103.8198°E" },
  { city: "Toronto", lat: 43.6532, lon: -79.3832, coords: "43.6532°N · 79.3832°W" },
  { city: "Chicago", lat: 41.8781, lon: -87.6298, coords: "41.8781°N · 87.6298°W" },
  { city: "Delhi NCR", lat: 28.6139, lon: 77.209, coords: "28.6139°N · 77.2090°E" },
];

const DOTS = [];
for (let lat = -75; lat <= 75; lat += 5) {
  for (let lon = -180; lon < 180; lon += 5) {
    DOTS.push([lat, lon]);
  }
}

export default function Globe() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    let R = 0;
    let running = true;
    let rot = 0.8;
    let t = 0;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = wrap.clientWidth;
      h = wrap.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      R = Math.min(w, h) * 0.38;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    const io = new IntersectionObserver(([entry]) => {
      running = entry.isIntersecting;
    });
    io.observe(wrap);

    const project = (lat, lon) => {
      const la = (lat * Math.PI) / 180;
      const lo = (lon * Math.PI) / 180 + rot;
      const x = Math.cos(la) * Math.sin(lo);
      const y = Math.sin(la);
      const z = Math.cos(la) * Math.cos(lo);
      return { x: w / 2 + x * R, y: h / 2 - y * R, z };
    };

    const draw = () => {
      raf = requestAnimationFrame(draw);
      if (!running) return;
      t += 1;
      if (!reduced) rot += 0.0022;
      ctx.clearRect(0, 0, w, h);

      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, R, 0, Math.PI * 2);
      ctx.stroke();

      for (const [lat, lon] of DOTS) {
        const p = project(lat, lon);
        if (p.z <= 0) continue;
        ctx.fillStyle = `rgba(255,255,255,${0.05 + p.z * 0.32})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 0.9 + p.z * 0.9, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const c of CENTRES) {
        const p = project(c.lat, c.lon);
        if (p.z <= 0.02) continue;
        const pulse = ((t / 70) % 1.6) / 1.6;
        ctx.strokeStyle = `rgba(59,130,246,${(1 - pulse) * 0.55 * p.z})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4 + pulse * 16, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = "#3B82F6";
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.2 + p.z * 1.2, 0, Math.PI * 2);
        ctx.fill();
        if (p.z > 0.3) {
          ctx.fillStyle = `rgba(255,255,255,${Math.min(1, p.z + 0.15)})`;
          ctx.font = "10px 'JetBrains Mono', monospace";
          ctx.fillText(c.city.toUpperCase(), p.x + 10, p.y + 3);
        }
      }
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
    };
  }, []);

  return (
    <Reveal>
      <div
        data-testid="globe-panel"
        className="mb-14 grid overflow-hidden border border-white/10 bg-surface/50 lg:grid-cols-2"
      >
        <div ref={wrapRef} className="relative h-[320px] sm:h-[440px]">
          <div className="absolute inset-0 bg-grid opacity-40" aria-hidden />
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 h-3/4 w-3/4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/10 blur-3xl"
            aria-hidden
          />
          <canvas ref={canvasRef} data-testid="globe-canvas" className="relative block" />
          <p className="absolute bottom-4 left-4 font-mono text-[9px] uppercase tracking-[0.25em] text-gray-600">
            FIG. 01 — GROUP DELIVERY NETWORK
          </p>
        </div>
        <div className="border-t border-white/10 p-6 sm:p-8 lg:border-l lg:border-t-0">
          <Overline>Live delivery map</Overline>
          <h3 className="mt-4 font-heading text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl">
            Six centres, plotted.{" "}
            <span className="text-gold">The sun never sets on delivery.</span>
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-gray-400">
            Every marker is a live Dignifyd capability centre. As one region signs off,
            the next signs on — work moves west to east, 24 hours a day, five days a
            week.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
            {CENTRES.map((c) => (
              <div key={c.city} data-testid={`globe-centre-${c.city.toLowerCase().replace(/\s+/g, "-")}`} className="group bg-ink p-4 transition-colors duration-300 hover:bg-surface">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                  {c.city}
                </p>
                <p className="mt-2 font-mono text-[10px] text-gray-500">{c.coords}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
            Follow-the-sun · 24/5 · Americas → EMEA → APAC
          </p>
        </div>
      </div>
    </Reveal>
  );
}
